import { SignInPrompt, WordsHappenButton, WordsHappenFormAlert } from '@words-happen/ui-components';
import type React from 'react';
import { useId } from 'react';
import {
  MessageScreen,
  messageScreenActionsClassName,
  messageScreenNoticeClassName,
  messageScreenStatusClassName,
} from '../../components';
import type { Resend } from '../use-resend';
import { VERIFICATION_MESSAGES } from './verification-messages';

export interface VerifyEmailScreenProps {
  /** The address the link went to, or null when it isn't known. */
  email: string | null;
  /** "Resend email": its cooldown, progress and outcome (useResend). */
  resend: Resend;
  /** Whether "I've verified" is checking. */
  checking?: boolean;
  /** Why the last check failed, e.g. still unverified. Shown above the actions in a WordsHappenFormAlert. */
  error?: string;
  /** "I've verified": checks again and, once verified, hands the player off to the game. */
  onCheck: () => void;
  onSignOut: () => void;
}

/**
 * "Check your email": an unverified player waits here until they have opened the verification link. There is no
 * way to change the address: a player who mistyped it signs out and registers again.
 */
export const VerifyEmailScreen: React.FC<VerifyEmailScreenProps> = ({
  email,
  resend,
  checking = false,
  error,
  onCheck,
  onSignOut,
}) => {
  const cooldownId = useId();
  const alert = error ?? resend.error;

  return (
    <MessageScreen
      title="Check your email"
      mood="excited"
      action={
        <div className={messageScreenActionsClassName}>
          {alert ? <WordsHappenFormAlert>{alert}</WordsHappenFormAlert> : null}
          <WordsHappenButton variant="coral" size="lg" loading={checking} onClick={onCheck}>
            I've verified
          </WordsHappenButton>
          <WordsHappenButton
            size="lg"
            loading={resend.sending}
            disabled={resend.cooldown > 0}
            aria-describedby={resend.cooldown > 0 ? cooldownId : undefined}
            onClick={resend.resend}
          >
            Resend email
          </WordsHappenButton>
          {resend.cooldown > 0 ? (
            <p id={cooldownId} className={messageScreenStatusClassName}>
              You can resend in {resend.cooldown}s
            </p>
          ) : null}
          <p role="status" className={messageScreenStatusClassName}>
            {resend.status}
          </p>
          <SignInPrompt prefix={null} action="Sign out" onAction={onSignOut} />
        </div>
      }
    >
      <p>
        We sent a link to {email ? <strong>{email}</strong> : 'your email address'}. Open it to verify your address,
        then come back here.
      </p>
      <p className={messageScreenNoticeClassName}>{VERIFICATION_MESSAGES.deadline}</p>
    </MessageScreen>
  );
};
